/**
 * Watches the chat composer text and, when it contains a strong
 * "week N" intent phrase (see detectMentionedWeeks for the patterns),
 * pre-attaches the matching plan from /documents/teaching-plans/ as a
 * conversation attachment. The attachment chip appears in the
 * composer's strip so the teacher can review or remove before sending.
 *
 * Mount as a no-render component inside AssistantRuntimeProvider so it
 * has access to useComposer / useComposerRuntime.
 *
 * Debounce: 700 ms of typing silence before scanning. Avoids fetch
 * spam mid-typing and lets the user finish the phrase before we react.
 *
 * Idempotence: every attempted week is recorded in a ref-Set, so once
 * we've tried to attach the week-3 plan we don't keep retrying on
 * subsequent keystrokes (even if the teacher later removes the chip
 * because they decided they didn't want it after all). The Set
 * resets on conversation change since the component remounts.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useComposer, useComposerRuntime } from "@assistant-ui/react";

import { detectMentionedWeeks } from "./detectMentionedWeeks";
import { PLANS_PATH, listDocs } from "./documentsApi";
import { attachPlanByFilename } from "./attachPlanToComposer";
import { planFilename } from "./serializeMarkdown";

const DEBOUNCE_MS = 700;

export function PlanAutoAttachWatcher(): null {
  const composer = useComposerRuntime();
  const qc = useQueryClient();
  const text = useComposer((s) => s.text);
  const attachmentsKey = useComposer((s) =>
    s.attachments.map((a) => a.name).sort().join("|"),
  );

  // Weeks we've already tried to attach this conversation. Prevents
  // re-attempts after the teacher removes a chip — they get one
  // auto-pick per phrase per session.
  const triedRef = useRef(new Set<number>());

  useEffect(() => {
    const weeks = detectMentionedWeeks(text);
    if (weeks.length === 0) return;

    const handle = window.setTimeout(async () => {
      // Fetch the plans tree via the React Query cache when possible,
      // otherwise hit the endpoint fresh. Either way we read the list
      // once per debounce cycle, not per detected week.
      let tree;
      try {
        tree = await qc.fetchQuery({
          queryKey: ["documents-tree", PLANS_PATH],
          queryFn: () => listDocs(PLANS_PATH),
          staleTime: 30_000,
        });
      } catch {
        // The picker surfaces fetch errors; if the tree call fails we
        // silently skip auto-attach instead of showing an error from
        // an invisible component.
        return;
      }
      const filesByName = new Set((tree.files ?? []).map((f) => f.name));
      const alreadyAttached = new Set(attachmentsKey ? attachmentsKey.split("|") : []);

      for (const week of weeks) {
        if (triedRef.current.has(week)) continue;
        // Match by the canonical week-NN_ prefix. The topic slug after
        // the underscore varies per plan; we pick the first one that
        // matches the week and isn't already on the composer.
        const matchName = [...filesByName].find((n) =>
          n.startsWith(`week-${String(week).padStart(2, "0")}_`),
        );
        if (!matchName) {
          // Try the exact filename with a generic slug just in case
          // the tree call missed it for a beat (newly created plan).
          // No-op if neither exists.
          const guess = planFilename({ week, topic: "" });
          if (!filesByName.has(guess)) continue;
        }
        const filename = matchName ?? planFilename({ week, topic: "" });
        if (alreadyAttached.has(filename)) {
          triedRef.current.add(week);
          continue;
        }
        triedRef.current.add(week);
        try {
          await attachPlanByFilename(composer, filename);
        } catch {
          // Same swallow rationale — invisible component, no toast UI.
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
    };
  }, [text, attachmentsKey, composer, qc]);

  return null;
}
